const API_URL = import.meta.env.VITE_GRAPHQL_API_URL;
const API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY;

// Helper function to make GraphQL requests
async function fetchGraphQL(query, variables = {}) {
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const jsonResponse = await response.json();
    if (jsonResponse.errors) {
      console.error('GraphQL Errors:', jsonResponse.errors);
      throw new Error('Error fetching data from GraphQL API.');
    }

    return jsonResponse.data;
  } catch (error) {
    console.error('Network or GraphQL Error:', error);
    throw error;
  }
}

// --- API Functions ---

// Get user's locale (language and country)
export function getUserLocale() {
    const locale = navigator.language || 'en-US'; // Default to en-US
    const [lang, country] = locale.split('-');
    return { lang: lang.toLowerCase(), country: (country || 'us').toLowerCase() };
}

// Query for all products with their relevant localization
export async function getAllProducts(nextToken = null) {
  const { lang, country } = getUserLocale();
  const query = `
    query GetAllProductsByLocalization($lang: String!, $country: String!, $nextToken: String) {
      getAllProductsByLocalization(lang: $lang, country: $country, limit: 20, nextToken: $nextToken) {
        items {
          sku
          imageUrl
          productStatus
          localizations {
            productName
            description
            price
            currency
          }
        }
        nextToken
      }
    }
  `;
  const data = await fetchGraphQL(query, { lang, country, nextToken });
  return data.getAllProductsByLocalization;
}

export async function searchProducts(search, nextToken = null) {
  const { lang, country } = getUserLocale();
  const query = `
    query SearchProducts($search: String!, $lang: String!, $country: String!, $nextToken: String) {
      searchProducts(search: $search, lang: $lang, country: $country, limit: 20, nextToken: $nextToken) {
        items {
          sku
          imageUrl
          productStatus
          localizations {
            productName
            description
            price
            currency
          }
        }
        nextToken
      }
    }
  `;
  const data = await fetchGraphQL(query, { search, lang, country, nextToken });
  return data.searchProducts;
}

// Query for all categories in a specific language
export async function getAllCategories(nextToken = null) {
  const { lang } = getUserLocale();
  const query = `
    query GetAllCategoriesByLanguage($lang: String!, $nextToken: String) {
      getAllCategoriesByLanguage(lang: $lang, limit: 20, nextToken: $nextToken) {
        items {
          category
          text
        }
        nextToken
      }
    }
  `;
  const data = await fetchGraphQL(query, { lang, nextToken });
  return data.getAllCategoriesByLanguage;
}

// Query for products within a specific category
export async function getProductsByCategory(category, limit = 20, nextToken = null) {
  const { lang, country } = getUserLocale();
  const query = `
    query GetProductsByCategory($category: String!, $lang: String!, $country: String!, $limit: Int, $nextToken: String) {
      getProductsByCategory(category: $category, lang: $lang, country: $country, limit: $limit, nextToken: $nextToken) {
        items {
          sku
          imageUrl
          productStatus
          localizations {
            productName
            description
            price
            currency
          }
        }
        nextToken 
      }
    }
  `;
  const data = await fetchGraphQL(query, { category, lang, country });
  return data.getProductsByCategory;
}

// Fetches one or more products by their SKUs
export async function getProductsBySku(skus) {
  const { lang, country } = getUserLocale();
  const query = `
    query GetProductsBySku($skus: [ID!]!, $lang: String, $country: String) {
      getProductsBySku(skus: $skus, lang: $lang, country: $country) {
        sku
        category
        imageUrl
        productStatus
        quantityInStock
        localizations {
          lang
          country
          productName
          description
          price
          currency
        }
      }
    }
  `;
  const data = await fetchGraphQL(query, { skus, lang, country });
  return data.getProductsBySku;
}

// Query for all categories in a specific language
export async function getAllCategoriesByLanguage(nextToken = null) {
  const { lang } = getUserLocale();
  const query = `
    query GetAllCategoriesByLanguage($lang: String!, $nextToken: String) {
      getAllCategoriesByLanguage(lang: $lang, limit: 20, nextToken: $nextToken) {
        items {
          category
          text
        }
        nextToken
        lastUpdated
      }
    }
  `;
  
  const data = await fetchGraphQL(query, { lang, nextToken });
  return data.getAllCategoriesByLanguage;
}

// Query to fetch metadata values
export async function getMetadata() {
  const query = `
    query GetMetadata {
      metadata {
        productsLastUpdated
        categoriesLastUpdated
      }
    }
  `;
    
  const data = await fetchGraphQL(query);
  return data.metadata;
}